import maplibregl from 'maplibre-gl'

interface OverpassElement {
  type: string
  id: number
  lat: number
  lon: number
  tags?: Record<string, string>
}

// Path data of the `Cctv` icon from lucide-react, so the map marker matches
// the toolbar button. Drawn on a 24x24 grid, stroked (not filled).
const CCTV_ICON_PATHS = [
  'M16.75 12h3.632a1 1 0 0 1 .894 1.447l-2.034 4.069a1 1 0 0 1-1.708.134l-2.124-2.97',
  'M17.106 9.053a1 1 0 0 1 .447 1.341l-3.106 6.211a1 1 0 0 1-1.342.447L3.61 12.3a2.92 2.92 0 0 1-1.3-3.91L3.69 5.6a2.92 2.92 0 0 1 3.92-1.3z',
  'M2 19h3.76a2 2 0 0 0 1.8-1.1L9 15',
  'M2 21v-4',
  'M7 9h.01',
]

const CARDINAL_TO_DEGREES: Record<string, number> = {
  N: 0,
  NNE: 22.5,
  NE: 45,
  ENE: 67.5,
  E: 90,
  ESE: 112.5,
  SE: 135,
  SSE: 157.5,
  S: 180,
  SSW: 202.5,
  SW: 225,
  WSW: 247.5,
  W: 270,
  WNW: 292.5,
  NW: 315,
  NNW: 337.5,
}

/**
 * `camera:direction` is degrees clockwise from north, or a cardinal name.
 * Multi-head cameras list several, semicolon-separated ("70;190;SW"); only
 * the first is drawn, since one feature can carry only one rotation.
 */
function parseDirection(value: string | undefined): number | null {
  if (value === undefined) return null
  const first = value.split(';')[0]?.trim()
  if (!first) return null
  const num = Number(first)
  if (!Number.isNaN(num)) return num
  return CARDINAL_TO_DEGREES[first.toUpperCase()] ?? null
}

// OSM tag values are third-party content, so they can't go into HTML raw.
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export class CameraOverlayManager {
  private map: maplibregl.Map
  private sourceId = 'surveillance-cameras-source'
  private layerId = 'surveillance-cameras-layer'
  private coneLayerId = 'surveillance-cameras-cone-layer'
  private abortController: AbortController | null = null
  private onLoadingChange: (loading: boolean) => void
  private onError: (message: string) => void
  private popup: maplibregl.Popup
  private popupEnabled = true

  constructor(
    map: maplibregl.Map,
    onLoadingChange: (loading: boolean) => void,
    onError: (message: string) => void
  ) {
    this.map = map
    this.onLoadingChange = onLoadingChange
    this.onError = onError
    this.popup = new maplibregl.Popup({
      closeButton: true,
      closeOnClick: true,
      maxWidth: '300px',
    })
    this.initLayers()
  }

  /**
   * Draws the lucide icon into a map image. It's stroked onto a canvas rather
   * than used via `text-field` because the glyph stacks served by /api/fonts
   * only contain the OpenMapTiles fonts.
   */
  private addCameraImage(id: string) {
    const grid = 24 // the icon's own coordinate system
    const scale = 4 // draw at 96px...
    const pixelRatio = 3 // ...and declare 3x density, so it shows at 32px
    const size = grid * scale
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.scale(scale, scale)
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    const paths = CCTV_ICON_PATHS.map((d) => new Path2D(d))

    // Every casing is laid down before any stroke, otherwise a later path's
    // casing would paint over an earlier path's stroke and break up the icon.
    ctx.strokeStyle = '#ffffff'
    ctx.lineWidth = 5
    for (const path of paths) ctx.stroke(path)

    ctx.strokeStyle = '#ef4444'
    ctx.lineWidth = 2
    for (const path of paths) ctx.stroke(path)

    const { data } = ctx.getImageData(0, 0, size, size)
    this.map.addImage(
      id,
      { width: size, height: size, data: new Uint8Array(data.buffer) },
      { pixelRatio }
    )
  }

  /**
   * Draws the field-of-view wedge. It points north (up) and has its apex at
   * the centre of the bitmap, so `icon-rotate` can take `camera:direction`
   * unmodified and pivots around the camera itself.
   */
  private addConeImage(id: string) {
    const pixelRatio = 3
    const size = 240 // => 80px on screen, a 40px reach around the camera
    const apex = size / 2
    const radius = apex
    const halfAngle = Math.PI / 6 // 60° spread

    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Canvas angles run clockwise from east, so north is -90°.
    const start = -Math.PI / 2 - halfAngle
    const end = -Math.PI / 2 + halfAngle

    const gradient = ctx.createRadialGradient(apex, apex, 0, apex, apex, radius)
    gradient.addColorStop(0, 'rgba(239, 68, 68, 0.55)')
    gradient.addColorStop(1, 'rgba(239, 68, 68, 0)')

    ctx.fillStyle = gradient
    ctx.beginPath()
    ctx.moveTo(apex, apex)
    ctx.arc(apex, apex, radius, start, end)
    ctx.closePath()
    ctx.fill()

    const { data } = ctx.getImageData(0, 0, size, size)
    this.map.addImage(
      id,
      { width: size, height: size, data: new Uint8Array(data.buffer) },
      { pixelRatio }
    )
  }

  private initLayers() {
    this.addCameraImage('camera-icon')
    this.addConeImage('camera-cone')

    this.map.addSource(this.sourceId, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })

    // Added first so it draws beneath the camera icon.
    this.map.addLayer({
      id: this.coneLayerId,
      type: 'symbol',
      source: this.sourceId,
      filter: ['==', ['get', 'hasDirection'], true],
      layout: {
        'icon-image': 'camera-cone',
        'icon-size': ['interpolate', ['linear'], ['zoom'], 10, 0.6, 18, 1],
        // `camera:direction` is already degrees clockwise from north, which is
        // what icon-rotate expects once the icon is aligned to the map.
        'icon-rotate': ['get', 'direction'],
        'icon-rotation-alignment': 'map',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    })

    this.map.addLayer({
      id: this.layerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        // Kept upright: the icon is asymmetric (wall mount at bottom-left), so
        // rotating it to camera:direction would leave many markers upside-down.
        // The direction is still listed in the popup's tags.
        'icon-image': 'camera-icon',
        'icon-size': ['interpolate', ['linear'], ['zoom'], 10, 0.6, 18, 1],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    })

    // Tap a camera to see its OSM tags (built for touch, so no hover state)
    this.map.on('click', [this.layerId, this.coneLayerId], (e) => {
      if (!this.popupEnabled) return
      if (!e.features || e.features.length === 0) return
      const props = e.features[0].properties
      if (!props) return
      const coords = (
        e.features[0].geometry as GeoJSON.Point
      ).coordinates.slice() as [number, number]
      const tags: Record<string, string> = props.tags
        ? JSON.parse(props.tags)
        : {}
      const rows = Object.entries(tags)
        .map(
          ([k, v]) =>
            `<tr><td style="padding:2px 8px 2px 0;color:#64748b;vertical-align:top;white-space:nowrap">${escapeHtml(k)}</td><td style="padding:2px 0">${escapeHtml(v)}</td></tr>`
        )
        .join('')
      const html = rows
        ? `<table style="font-size:13px;line-height:1.5;border-collapse:collapse">${rows}</table>`
        : `<div style="font-size:13px;color:#64748b">No tags</div>`
      this.popup.setLngLat(coords).setHTML(html).addTo(this.map)
    })
  }

  /**
   * While the user is placing a pin, taps should fall through to the map
   * instead of opening a camera popup.
   */
  setPopupEnabled(enabled: boolean) {
    this.popupEnabled = enabled
    if (!enabled) this.popup.remove()
  }

  async fetchCameras() {
    if (this.abortController) {
      this.abortController.abort()
    }
    this.abortController = new AbortController()

    const bounds = this.map.getBounds()
    const query = `[out:json][timeout:10];node["man_made"="surveillance"](${bounds.getSouth()},${bounds.getWest()},${bounds.getNorth()},${bounds.getEast()});out body;`

    this.onLoadingChange(true)

    try {
      const response = await fetch('https://overpass-api.de/api/interpreter', {
        method: 'POST',
        body: `data=${encodeURIComponent(query)}`,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        signal: this.abortController.signal,
      })

      if (!response.ok) {
        const err = new Error(`Overpass API error: ${response.status}`)
        ;(err as Error & { status: number }).status = response.status
        throw err
      }

      const data: { elements: OverpassElement[] } = await response.json()

      const features = data.elements
        .filter((el) => el.lat !== undefined && el.lon !== undefined)
        .map((el) => {
          const dir = parseDirection(el.tags?.['camera:direction'])
          return {
            type: 'Feature' as const,
            properties: {
              id: el.id,
              tags: JSON.stringify(el.tags || {}),
              hasDirection: dir !== null,
              direction: dir ?? 0,
            },
            geometry: {
              type: 'Point' as const,
              coordinates: [el.lon, el.lat],
            },
          }
        })

      const source = this.map.getSource(
        this.sourceId
      ) as maplibregl.GeoJSONSource
      if (source) {
        source.setData({ type: 'FeatureCollection', features })
      }

      return features.length
    } catch (err) {
      if ((err as Error).name === 'AbortError') return null
      console.error('Failed to fetch surveillance cameras:', err)
      const status = (err as { status?: number }).status
      let msg = 'cameraError'
      // Overpass' front-end also answers 406 when it's shedding load, not just 429.
      if (status === 429 || status === 406) {
        msg = 'cameraRateLimited'
      } else if (status === 504 || status === 408) {
        msg = 'cameraTimeout'
      }
      this.onError(msg)
      return null
    } finally {
      this.onLoadingChange(false)
    }
  }

  destroy() {
    if (this.abortController) {
      this.abortController.abort()
    }
    this.popup.remove()
    if (this.map.getLayer(this.layerId)) this.map.removeLayer(this.layerId)
    if (this.map.getLayer(this.coneLayerId))
      this.map.removeLayer(this.coneLayerId)
    if (this.map.getSource(this.sourceId)) this.map.removeSource(this.sourceId)
  }
}
